import { useEffect, useState } from 'react';
import { useChangerQuantite, type Order, type RewardProgress } from '@/lib/orders';
import { formatMoney } from '@/lib/utils';

/**
 * Panier persistant — colonne de droite de l'écran de commande (plan §5.4).
 * Les totaux affichés viennent TOUJOURS du serveur : rien n'est recalculé ici,
 * pour qu'aucun écart ne puisse apparaître entre l'écran et le bon de commande.
 */
export default function Panier({
  order,
  onSoumettre,
  onAnnuler,
  annulEnCours = false,
  rewards,
}: {
  order: Order;
  onSoumettre: () => void;
  onAnnuler?: () => void;
  annulEnCours?: boolean;
  rewards?: RewardProgress[];
}) {
  const changer = useChangerQuantite(order.id);
  const vide = order.lines.length === 0;

  // Cadeaux : on n'affiche que les fournisseurs où le client a un cumul d'achats
  // (> 0) — sinon un REP_SUPER voit une carte « il reste X $ » par fournisseur, ce
  // qui remplit tout le panier. Le tout est replié/dépliable.
  const cadeauxActifs = (rewards ?? []).filter((g) => Number(g.cumulative) > 0);
  const [cadeauxOuverts, setCadeauxOuverts] = useState(true);

  return (
    // Portrait (tablette) : tout le panier défile d'un bloc — on peut faire défiler
    // jusqu'au bout de la liste pour vérifier/corriger chaque produit, le pied
    // (total + soumettre) venant après les articles. Paysage (lg) : mise en page en
    // colonne, articles défilants et pied épinglé (la hauteur le permet).
    <aside className="bg-card h-full overflow-y-auto lg:flex lg:flex-col lg:overflow-hidden">
      <header className="border-b p-4">
        <p className="text-muted-foreground text-sm">Client</p>
        <p className="text-lg font-semibold">{order.customer.name}</p>
        <p className="text-muted-foreground font-mono text-xs">{order.customer.custNum}</p>
      </header>

      {/* Entête client + progression cadeaux + articles partagent UNE zone défilante :
          en portrait (tablette), la bannière cadeaux ne « fige » plus l'espace et
          n'écrase plus la liste des articles — tout défile ensemble sous l'entête. */}
      <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {/* Progression cadeaux (fournisseurs avec achats seulement), repliable. Le
            cumul inclut le panier en cours. Défile avec les articles pour laisser
            voir le contenu du panier. */}
        {cadeauxActifs.length > 0 && (
          <div className="border-b">
            <button
              type="button"
              onClick={() => setCadeauxOuverts((v) => !v)}
              aria-expanded={cadeauxOuverts}
              className="flex w-full items-center gap-2 p-3 text-left"
            >
              <span aria-hidden className="text-muted-foreground text-lg leading-none">
                {cadeauxOuverts ? '▾' : '▸'}
              </span>
              <span className="font-medium">🎁 Cadeaux à venir ({cadeauxActifs.length})</span>
            </button>
            {cadeauxOuverts && (
              <div className="space-y-2 px-3 pb-3">
                {cadeauxActifs.map((g) => (
                  <div key={g.groupId} className="bg-primary/5 rounded-lg p-2 text-sm">
                    <div className="font-medium">🎁 {g.groupName}</div>
                    {g.next ? (
                      <div className="text-muted-foreground">
                        Il reste{' '}
                        <span className="text-foreground font-semibold tabular-nums">
                          {formatMoney(g.next.remaining)}
                        </span>{' '}
                        avant :{' '}
                        <span className="text-foreground font-medium">{g.next.giftName}</span>
                      </div>
                    ) : (
                      <div className="font-medium text-green-700 dark:text-green-400">
                        Tous les cadeaux débloqués !
                      </div>
                    )}
                    {g.gifts.some((t) => t.reached) && (
                      <div className="mt-0.5 line-clamp-2 text-xs text-green-700 dark:text-green-400">
                        Débloqué :{' '}
                        {g.gifts
                          .filter((t) => t.reached)
                          .map((t) => t.giftName)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="p-3">
          {vide ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Panier vide. Touchez un produit à gauche pour l'ajouter.
            </p>
          ) : (
            <ul className="divide-y">
              {order.lines.map((l) => (
                <li key={l.id} className="px-1 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 text-sm leading-snug">
                      <span className="font-mono text-xs font-semibold">{l.partNumber}</span>{' '}
                      {l.description}
                    </p>
                    <p className="shrink-0 font-semibold tabular-nums">{formatMoney(l.lineTotal)}</p>
                  </div>

                  {l.promoLabel && (
                    <p className="text-xs font-medium text-green-700 dark:text-green-400">
                      {l.promoLabel} · −{formatMoney(l.discountAmount)}
                    </p>
                  )}

                  <div className="mt-1.5 flex items-center gap-2">
                    {/* Boutons −/+ ou saisie directe de la quantité. */}
                    <QtyControl
                      qty={l.qty}
                      partNumber={l.partNumber}
                      disabled={changer.isPending}
                      onChange={(qty) => changer.mutate({ lineId: l.id, qty })}
                    />
                    <span className="text-muted-foreground ml-auto text-xs">
                      {formatMoney(l.unitPrice)} / {l.uom}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <footer className="space-y-3 border-t p-4">
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Sous-total</dt>
            <dd className="tabular-nums">{formatMoney(order.subtotal)}</dd>
          </div>
          {order.discountTotal !== '0.00' && (
            <div className="flex justify-between text-green-700 dark:text-green-400">
              <dt>Rabais</dt>
              <dd className="tabular-nums">−{formatMoney(order.discountTotal)}</dd>
            </div>
          )}
        </dl>

        {/* Total en très gros : c'est le chiffre que le rep montre au client. */}
        <div className="flex items-baseline justify-between border-t pt-3">
          <span className="font-medium">Total</span>
          <span className="text-3xl font-bold tabular-nums">{formatMoney(order.total)}</span>
        </div>
        <p className="text-muted-foreground text-xs">Montants avant taxes.</p>

        <button
          onClick={onSoumettre}
          disabled={vide}
          className="bg-primary text-primary-foreground h-16 w-full rounded-lg text-lg font-semibold disabled:opacity-40"
        >
          Réviser et soumettre
        </button>

        {/* Annulation d'un brouillon abandonné. Discret, sous la soumission, pour
            ne pas concurrencer l'action principale. Le serveur autorise le rep
            propriétaire ou un admin. */}
        {onAnnuler && (
          <button
            onClick={onAnnuler}
            disabled={annulEnCours}
            className="text-muted-foreground h-12 w-full rounded-lg text-sm font-medium underline-offset-4 hover:underline disabled:opacity-40"
          >
            {annulEnCours ? 'Annulation…' : 'Annuler la commande'}
          </button>
        )}
      </footer>
    </aside>
  );
}

/**
 * Contrôle de quantité : boutons −/+ ET saisie directe. Le champ est en état local
 * et ne valide qu'à la perte de focus (ou Entrée), pour ne pas déclencher une
 * requête à chaque frappe. La valeur est bornée à [0, 9999] ; 0 retire la ligne
 * (le serveur supprime la ligne quand la quantité tombe à zéro).
 */
function QtyControl({
  qty,
  partNumber,
  disabled,
  onChange,
}: {
  qty: number;
  partNumber: string;
  disabled: boolean;
  onChange: (qty: number) => void;
}) {
  const [val, setVal] = useState(String(qty));
  // Resynchronise si la quantité change ailleurs (−/+, recalcul serveur).
  useEffect(() => setVal(String(qty)), [qty]);

  function commit() {
    const raw = Math.floor(Number(val));
    if (!Number.isFinite(raw)) {
      setVal(String(qty));
      return;
    }
    const n = Math.min(9999, Math.max(0, raw));
    if (n !== qty) onChange(n);
    else setVal(String(qty));
  }

  return (
    <>
      <button
        type="button"
        aria-label={`Retirer une unité de ${partNumber}`}
        onClick={() => onChange(qty - 1)}
        disabled={disabled}
        className="border-input h-11 w-11 shrink-0 rounded-lg border text-xl font-bold disabled:opacity-40"
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={9999}
        aria-label={`Quantité de ${partNumber}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        disabled={disabled}
        className="border-input bg-background h-11 w-14 rounded-lg border text-center text-lg font-semibold tabular-nums disabled:opacity-40"
      />
      <button
        type="button"
        aria-label={`Ajouter une unité de ${partNumber}`}
        onClick={() => onChange(qty + 1)}
        disabled={disabled}
        className="border-input h-11 w-11 shrink-0 rounded-lg border text-xl font-bold disabled:opacity-40"
      >
        +
      </button>
    </>
  );
}
